/**
 * Helpers shared by the scripts nginx injects into static Candidate and
 * Committee pages (money-flow.ts, sites#178; entity-graph.ts, sites#249).
 */

/** FEC ID from the page header (`<p class="fec-id">FEC ID: C00010603</p>`). */
export function readFecId(doc: Document): string | null {
  const el = doc.querySelector('header p.fec-id, p.fec-id');
  const m = el?.textContent?.match(/FEC ID:\s*([A-Z0-9]+)/i);
  if (!m) return null;
  const id = m[1].toUpperCase();
  return /^C\d{8}$/.test(id) || /^[HSP][0-9A-Z]{8}$/.test(id) ? id : null;
}

// Party colours as in the news chips (sites#147): DEM/DFL blue, REP red.
const DEM = [37, 99, 235]; // #2563eb
const REP = [220, 38, 38]; // #dc2626

function mix(rgb: number[], weight: number): string {
  // Blend toward white: weight 1 is the full party colour, 0 is white.
  const w = Math.max(0.35, Math.min(1, weight));
  const c = rgb.map((v) => Math.round(255 - (255 - v) * w));
  return `rgb(${c[0]}, ${c[1]}, ${c[2]})`;
}

/**
 * Party colour: the party code first, then (committees) the party-affinity
 * margin, tinted by its strength. Null when there is no party signal.
 */
export function partyColor(party: string | null, affinityDem: number | null, affinityRep: number | null): string | null {
  const code = (party || '').trim().toUpperCase();
  if (code === 'DEM' || code === 'DFL') return mix(DEM, 1);
  if (code === 'REP') return mix(REP, 1);
  const margin = (affinityDem ?? 0) - (affinityRep ?? 0);
  if (Math.abs(margin) >= 0.1) return mix(margin > 0 ? DEM : REP, Math.abs(margin));
  return null;
}

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
}

export function usd(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}
